import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import UnfoldSalesLogo from './UnfoldSalesLogo';

const Header = () => {
  const { user, logout } = useAuth();
  const [newOptCats, setNewOptCats] = useState([]);
  const [apptNotifs, setApptNotifs] = useState([]);
  const [showBell, setShowBell] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' });
  const [pwLoading, setPwLoading] = useState(false);
  const [pwError, setPwError] = useState('');
  const bellRef = React.useRef(null);
  const profileRef = React.useRef(null);

  const canSeeNotifications = user && (
    user.role === 'teamleiter' ||
    user.role === 'vertriebler' ||
    user.role === 'backoffice' ||
    user.role === 'vertriebsleiter' ||
    user.role === 'admin'
  );

  const totalNotifCount = newOptCats.length + apptNotifs.length;

  useEffect(() => {
    if (!canSeeNotifications) return;
    const fetchNotifications = async () => {
      try {
        const res = await axios.get(`${API}/notifications/new-optional-categories`);
        setNewOptCats(res.data || []);
      } catch {}
      try {
        const res2 = await axios.get(`${API}/notifications/appointments`);
        setApptNotifs(res2.data || []);
      } catch {}
    };
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [user]);

  const markSeenAndClose = async () => {
    setShowBell(false);
    if (newOptCats.length > 0) {
      try {
        await axios.post(`${API}/notifications/mark-seen`, { category_ids: newOptCats.map(c => c.id) });
        setNewOptCats([]);
      } catch {}
    }
    if (apptNotifs.length > 0) {
      try {
        await axios.post(`${API}/notifications/appointments/mark-read`, { notification_ids: apptNotifs.map(n => n.id) });
        setApptNotifs([]);
      } catch {}
    }
  };

  useEffect(() => {
    const handleClick = (e) => {
      if (bellRef.current && !bellRef.current.contains(e.target)) markSeenAndClose();
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [newOptCats, apptNotifs, showBell]);

  useEffect(() => {
    const handleClick = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) setShowProfileMenu(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showProfileMenu]);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwError('');
    if (pwForm.next !== pwForm.confirm) { setPwError('Die neuen Passwörter stimmen nicht überein'); return; }
    if (pwForm.next.length < 8) { setPwError('Das neue Passwort muss mindestens 8 Zeichen lang sein'); return; }
    setPwLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: pwForm.current, new_password: pwForm.next });
      toast.success('Passwort erfolgreich geändert!');
      setShowChangePassword(false);
      setPwForm({ current: '', next: '', confirm: '' });
    } catch(err) {
      setPwError(err.response?.data?.detail || 'Fehler beim Ändern des Passworts');
    }
    setPwLoading(false);
  };

  const handleCategoryNotificationClick = (cat) => {
    markSeenAndClose();
    window.dispatchEvent(new CustomEvent('navigateToDashboard', { detail: { categoryId: cat.id } }));
  };

  const handleApptNotificationClick = () => {
    markSeenAndClose();
    // Navigate to exam booking tab
    window.dispatchEvent(new CustomEvent('navigateToExamBooking'));
  };

  const handleBellClick = () => {
    if (showBell) {
      markSeenAndClose();
    } else {
      setShowBell(true);
    }
  };

  const handleLogoClick = () => {
    window.location.href = '/';
  };

  return (
    <>
    {/* Password Change Modal */}
    {showChangePassword && (
      <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm">
          <h3 className="text-lg font-bold text-slate-900 mb-1">Passwort ändern</h3>
          <p className="text-sm text-slate-500 mb-5">Bitte gib dein aktuelles und neues Passwort ein.</p>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Aktuelles Passwort</label>
              <input type="password" value={pwForm.current}
                onChange={e => setPwForm(f => ({...f, current: e.target.value}))}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                placeholder="••••••••" autoFocus data-testid="pw-current-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Neues Passwort</label>
              <input type="password" value={pwForm.next}
                onChange={e => setPwForm(f => ({...f, next: e.target.value}))}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                placeholder="Mindestens 8 Zeichen" data-testid="pw-new-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Neues Passwort wiederholen</label>
              <input type="password" value={pwForm.confirm}
                onChange={e => setPwForm(f => ({...f, confirm: e.target.value}))}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                placeholder="••••••••" data-testid="pw-confirm-input" />
            </div>
            {pwError && <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{pwError}</p>}
            <div className="flex gap-3 justify-end pt-1">
              <button type="button" onClick={() => { setShowChangePassword(false); setPwForm({ current:'', next:'', confirm:'' }); setPwError(''); }}
                className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 font-medium">
                Abbrechen
              </button>
              <button type="submit" disabled={pwLoading}
                className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg disabled:opacity-50 transition-colors"
                data-testid="pw-submit-btn">
                {pwLoading ? 'Speichern...' : 'Speichern'}
              </button>
            </div>
          </form>
        </div>
      </div>
    )}

    <header className="bg-[#1a4db7] text-white py-2.5 px-4 shadow-lg border-b border-blue-800/40">
      <div className="container mx-auto flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-3 cursor-pointer" onClick={handleLogoClick}>
            <UnfoldSalesLogo size="xlarge" onClick={handleLogoClick} />
            <div>
              <h1 className="text-xl font-bold tracking-tight">unfold sales</h1>
              <span className="text-xs text-blue-200/80">Digitale Schulungsakademie</span>
            </div>
          </div>
        </div>
        {user && (
          <div className="flex items-center space-x-3">
            {canSeeNotifications && (
              <div className="relative" ref={bellRef}>
                <button
                  onClick={handleBellClick}
                  className="relative p-2 rounded-lg hover:bg-white/10 transition-colors"
                  data-testid="notification-bell"
                  title="Neue Inhalte"
                >
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                  </svg>
                  {totalNotifCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-xs font-bold w-4 h-4 rounded-full flex items-center justify-center">
                      {totalNotifCount > 9 ? '9+' : totalNotifCount}
                    </span>
                  )}
                </button>
                {showBell && (
                  <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-2xl border border-gray-100 z-50 overflow-hidden">
                    <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-4 py-3">
                      <p className="text-white text-sm font-semibold">Benachrichtigungen</p>
                      <p className="text-blue-100 text-xs mt-0.5">{totalNotifCount > 0 ? `${totalNotifCount} ungelesen` : 'Alles auf dem neuesten Stand'}</p>
                    </div>

                    {/* Appointment change notifications */}
                    {apptNotifs.filter(n => n.type !== 'lead_followup').length > 0 && (
                      <div>
                        <div className="px-4 py-1.5 bg-orange-50 border-b border-orange-100">
                          <p className="text-xs font-semibold text-orange-600 uppercase tracking-wide">Terminänderungen</p>
                        </div>
                        <div className="divide-y divide-gray-50">
                          {apptNotifs.filter(n => n.type !== 'lead_followup').map(notif => (
                            <button
                              key={notif.id}
                              onClick={handleApptNotificationClick}
                              className="w-full px-4 py-3 hover:bg-orange-50 transition-colors text-left"
                              data-testid="appt-notification-item"
                            >
                              <div className="flex items-start gap-3">
                                <span className="flex-shrink-0 mt-0.5">
                                  {notif.type === 'appointment_cancelled' ? (
                                    <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/>
                                    </svg>
                                  ) : (
                                    <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                                    </svg>
                                  )}
                                </span>
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm font-semibold text-gray-800">{notif.title}</p>
                                  <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{notif.message}</p>
                                  {notif.type === 'appointment_cancelled' && (
                                    <p className="text-xs text-blue-500 mt-1 font-medium">Neuen Termin buchen →</p>
                                  )}
                                  {notif.type === 'appointment_rescheduled' && (
                                    <p className="text-xs text-orange-500 mt-1 font-medium">Termin ansehen →</p>
                                  )}
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Lead Wiedervorlage notifications */}
                    {apptNotifs.filter(n => n.type === 'lead_followup').length > 0 && (
                      <div>
                        <div className="px-4 py-1.5 bg-indigo-50 border-b border-indigo-100">
                          <p className="text-xs font-semibold text-indigo-600 uppercase tracking-wide">Wiedervorlagen</p>
                        </div>
                        <div className="divide-y divide-gray-50">
                          {apptNotifs.filter(n => n.type === 'lead_followup').map(notif => (
                            <button
                              key={notif.id}
                              onClick={() => { markSeenAndClose(); window.dispatchEvent(new CustomEvent('navigateToLeads')); }}
                              className="w-full px-4 py-3 hover:bg-indigo-50 transition-colors text-left"
                              data-testid="lead-followup-notification-item"
                            >
                              <div className="flex items-start gap-3">
                                <span className="flex-shrink-0 mt-0.5">
                                  <svg className="w-5 h-5 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                                  </svg>
                                </span>
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm font-semibold text-gray-800">{notif.title}</p>
                                  <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{notif.message}</p>
                                  <p className="text-xs text-indigo-500 mt-1 font-medium">Lead ansehen →</p>
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* New category notifications */}
                    {newOptCats.length > 0 && (
                      <div>
                        <div className="px-4 py-1.5 bg-blue-50 border-b border-blue-100">
                          <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide">Neue Schulungsinhalte</p>
                        </div>
                        <div className="divide-y divide-gray-50">
                          {newOptCats.map(cat => (
                            <button
                              key={cat.id}
                              onClick={() => handleCategoryNotificationClick(cat)}
                              className="w-full px-4 py-3 hover:bg-blue-50 transition-colors text-left"
                            >
                              <div className="flex items-center gap-3">
                                <span className="text-xl flex-shrink-0">{cat.icon || '📂'}</span>
                                <div className="min-w-0">
                                  <p className="text-sm font-semibold text-gray-800 truncate">{cat.title}</p>
                                  <p className="text-xs text-blue-500 mt-0.5">Jetzt ansehen →</p>
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {totalNotifCount === 0 && (
                      <div className="px-4 py-6 text-center">
                        <svg className="w-8 h-8 text-gray-300 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                        </svg>
                        <p className="text-sm text-gray-400">Alles gesehen!</p>
                      </div>
                    )}
                    <div className="px-4 py-2 bg-gray-50 border-t border-gray-100">
                      <p className="text-xs text-gray-400 text-center">Klicken zum Schließen & als gelesen markieren</p>
                    </div>
                  </div>
                )}
              </div>
            )}
            <div className="hidden md:flex items-center space-x-2 relative" ref={profileRef}>
              <button
                onClick={() => setShowProfileMenu(v => !v)}
                className="flex items-center space-x-2 hover:bg-white/10 rounded-lg px-2 py-1 transition-colors"
                data-testid="profile-menu-btn"
              >
                <div className="w-8 h-8 bg-white/20 border border-white/30 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-semibold">
                    {user.name.split(' ').map(n => n[0]).join('')}
                  </span>
                </div>
                <div className="text-sm text-left">
                  <div className="font-medium">{user.name}</div>
                  <div className="text-blue-200/80 capitalize text-xs">{user.role}</div>
                </div>
                <svg className="w-3.5 h-3.5 text-white/60 ml-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {showProfileMenu && (
                <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-xl shadow-xl border border-slate-100 py-1 z-50">
                  <button
                    onClick={() => { setShowProfileMenu(false); setShowChangePassword(true); }}
                    className="w-full px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2.5 transition-colors"
                    data-testid="change-password-menu-item"
                  >
                    <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"/>
                    </svg>
                    Passwort ändern
                  </button>
                  <div className="border-t border-slate-100 my-1" />
                  <button
                    onClick={() => { setShowProfileMenu(false); logout(); }}
                    className="w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2.5 transition-colors"
                    data-testid="logout-profile-menu"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    Abmelden
                  </button>
                </div>
              )}
            </div>
            <button
              onClick={logout}
              className="flex items-center gap-1.5 bg-white/10 hover:bg-white/20 border border-white/20 px-3 py-1.5 rounded-lg text-sm font-medium transition-all text-white/90 hover:text-white md:hidden"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              <span className="hidden sm:inline">Abmelden</span>
            </button>
          </div>
        )}
      </div>
    </header>
    </>
  );
};

export default Header;
